import { validate2DArray, validateNumeric2DArray, validateNumericArray } from './validation.js';

export function transpose(matrix) {
  validate2DArray(matrix, 'matrix');
  if (matrix.length === 0 || matrix[0].length === 0) {
    return [];
  }
  return matrix[0].map((_, colIndex) => matrix.map(row => row[colIndex]));
}

export function dotProduct(a, b) {
  validateNumericArray(a, 'vector a');
  validateNumericArray(b, 'vector b');
  if (a.length !== b.length) {
    throw new Error(`Vectors must have same length. Got ${a.length} and ${b.length}`);
  }
  return a.reduce((sum, val, i) => sum + val * b[i], 0);
}

export function matrixMultiply(A, B) {
  validateNumeric2DArray(A, 'matrix A');
  validateNumeric2DArray(B, 'matrix B');
  
  const rowsA = A.length;
  const colsA = A[0].length;
  const rowsB = B.length;
  const colsB = B[0].length;
  
  if (colsA !== rowsB) {
    throw new Error(`Cannot multiply matrices: columns of A (${colsA}) must equal rows of B (${rowsB})`);
  }
  
  const result = Array(rowsA).fill(0).map(() => Array(colsB).fill(0));
  
  for (let i = 0; i < rowsA; i++) {
    for (let j = 0; j < colsB; j++) {
      for (let k = 0; k < colsA; k++) {
        result[i][j] += A[i][k] * B[k][j];
      }
    }
  }
  
  return result;
}

export function matrixVectorMultiply(matrix, vector) {
  validateNumeric2DArray(matrix, 'matrix');
  validateNumericArray(vector, 'vector');
  
  if (matrix[0].length !== vector.length) {
    throw new Error(`Matrix columns (${matrix[0].length}) must equal vector length (${vector.length})`);
  }
  
  return matrix.map(row => dotProduct(row, vector));
}

export function addMatrices(A, B) {
  validateNumeric2DArray(A, 'matrix A');
  validateNumeric2DArray(B, 'matrix B');
  
  if (A.length !== B.length || A[0].length !== B[0].length) {
    throw new Error('Matrices must have the same dimensions for addition');
  }
  
  return A.map((row, i) => row.map((val, j) => val + B[i][j]));
}

export function scalarMultiply(matrix, scalar) {
  if (Array.isArray(matrix[0])) {
    validateNumeric2DArray(matrix, 'matrix');
    return matrix.map(row => row.map(val => val * scalar));
  } else {
    validateNumericArray(matrix, 'vector');
    return matrix.map(val => val * scalar);
  }
}

export function identity(n) {
  return Array(n).fill(0).map((_, i) => 
    Array(n).fill(0).map((_, j) => i === j ? 1 : 0)
  );
}

export function inverse(matrix, options = {}) {
  validateNumeric2DArray(matrix, 'matrix');
  const n = matrix.length;
  
  if (n !== matrix[0].length) {
    throw new Error('Matrix must be square for inversion');
  }
  
  const regularization = options.regularization || 0;
  const augmented = matrix.map((row, i) => [...row, ...identity(n)[i]]);
  
  if (regularization > 0) {
    for (let i = 0; i < n; i++) {
      augmented[i][i] += regularization;
    }
  }
  
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }
    
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
    
    const pivot = augmented[i][i];
    if (Math.abs(pivot) < 1e-10) {
      throw new Error('Matrix is singular or nearly singular and cannot be inverted. Consider using Ridge regression or adding regularization.');
    }
    
    for (let j = 0; j < 2 * n; j++) {
      augmented[i][j] /= pivot;
    }
    
    for (let k = 0; k < n; k++) {
      if (k !== i) {
        const factor = augmented[k][i];
        for (let j = 0; j < 2 * n; j++) {
          augmented[k][j] -= factor * augmented[i][j];
        }
      }
    }
  }
  
  return augmented.map(row => row.slice(n));
}

export function euclideanDistance(a, b) {
  validateNumericArray(a, 'vector a');
  validateNumericArray(b, 'vector b');
  
  if (a.length !== b.length) {
    throw new Error(`Vectors must have same length. Got ${a.length} and ${b.length}`);
  }
  
  return Math.sqrt(a.reduce((sum, val, i) => sum + Math.pow(val - b[i], 2), 0));
}

export function covariance(X) {
  validateNumeric2DArray(X, 'X');
  
  const n = X.length;
  const p = X[0].length;
  
  const means = Array(p).fill(0);
  for (let j = 0; j < p; j++) {
    for (let i = 0; i < n; i++) {
      means[j] += X[i][j];
    }
    means[j] /= n;
  }
  
  const cov = Array(p).fill(0).map(() => Array(p).fill(0));
  
  for (let i = 0; i < p; i++) {
    for (let j = 0; j < p; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += (X[k][i] - means[i]) * (X[k][j] - means[j]);
      }
      cov[i][j] = sum / (n - 1);
    }
  }
  
  return cov;
}
